import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Fade,
  Collapse,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ClockIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Psychology as AIIcon,
  Description as DocIcon,
  CloudUpload as UploadIcon,
  Search as AnalyzeIcon,
  Architecture as ArchIcon,
  TableChart as TableIcon,
  PersonOutlineOutlined as PersonIcon,
  WorkOutlineOutlined as ProjectIcon,
  TimerOutlined as TimerIcon,
} from '@mui/icons-material';

// AI stage definitions — maps progress % ranges to human-readable activity
const AI_STAGES = [
  {
    range: [0, 15],
    icon: <AnalyzeIcon sx={{ fontSize: 18 }} />,
    label: 'Analyzing scoping notes',
    detail: 'Parsing project requirements, identifying key technical components and stakeholders...',
    color: '#6366f1',
  },
  {
    range: [15, 35],
    icon: <AIIcon sx={{ fontSize: 18 }} />,
    label: 'Mapping project phases',
    detail: 'Structuring 6-phase delivery plan, aligning AWS services to project objectives...',
    color: '#8b5cf6',
  },
  {
    range: [35, 55],
    icon: <ArchIcon sx={{ fontSize: 18 }} />,
    label: 'Generating technical plan',
    detail: 'Writing Section 3 — detailed implementation steps, architecture decisions, integrations...',
    color: '#a855f7',
  },
  {
    range: [55, 72],
    icon: <TableIcon sx={{ fontSize: 18 }} />,
    label: 'Building milestones & deliverables',
    detail: 'Calculating phase durations, generating 30–40 project deliverables, cost breakdown...',
    color: '#ec4899',
  },
  {
    range: [72, 88],
    icon: <DocIcon sx={{ fontSize: 18 }} />,
    label: 'Formatting Word document',
    detail: 'Applying professional styles, building tables, inserting cover page and TOC...',
    color: '#f59e0b',
  },
  {
    range: [88, 100],
    icon: <UploadIcon sx={{ fontSize: 18 }} />,
    label: 'Uploading & preparing download',
    detail: 'Storing document securely in S3, generating time-limited download link...',
    color: '#10b981',
  },
];

// Animated typing dots
const TypingDots = () => {
  return (
    <Box sx={{ display: 'inline-flex', gap: '3px', alignItems: 'center', ml: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 4,
            height: 4,
            borderRadius: '50%',
            backgroundColor: 'currentColor',
            opacity: 0.7,
            animation: 'pulse-dot 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes pulse-dot': {
              '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
              '40%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  );
};

// Single stage row in the activity feed
const StageRow = ({ stage, status }) => {
  // status: 'done' | 'active' | 'pending'
  const isDone = status === 'done';
  const isActive = status === 'active';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1,
        opacity: isDone ? 0.7 : isActive ? 1 : 0.35,
        transition: 'opacity 0.4s ease',
      }}
    >
      {/* Icon / spinner */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: 0.25,
          background: isDone
            ? 'rgba(16, 185, 129, 0.15)'
            : isActive
            ? `rgba(${hexToRgb(stage.color)}, 0.15)`
            : 'rgba(0,0,0,0.05)',
          border: `1.5px solid ${
            isDone ? '#10b981' : isActive ? stage.color : 'transparent'
          }`,
          transition: 'all 0.4s ease',
        }}
      >
        {isDone ? (
          <CheckIcon sx={{ fontSize: 16, color: '#10b981' }} />
        ) : isActive ? (
          <Box sx={{ color: stage.color, display: 'flex' }}>{stage.icon}</Box>
        ) : (
          <Box sx={{ color: '#9ca3af', display: 'flex' }}>{stage.icon}</Box>
        )}
      </Box>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isActive ? 600 : 500,
            color: isDone ? '#6b7280' : isActive ? '#111827' : '#9ca3af',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {stage.label}
          {isActive && <TypingDots />}
        </Typography>
        {isActive && (
          <Fade in timeout={300}>
            <Typography
              variant="caption"
              sx={{ color: '#6b7280', lineHeight: 1.5, display: 'block', mt: 0.25 }}
            >
              {stage.detail}
            </Typography>
          </Fade>
        )}
      </Box>

      {/* Time badge for done */}
      {isDone && (
        <Chip
          label="Done"
          size="small"
          sx={{
            height: 20,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            color: '#059669',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
};

// Helper: hex color to rgb string for rgba()
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '37, 99, 235';
}

// Get current stage index from progress
function getActiveStageIndex(progress) {
  for (let i = 0; i < AI_STAGES.length; i++) {
    const [min, max] = AI_STAGES[i].range;
    if (progress >= min && progress < max) return i;
  }
  return AI_STAGES.length - 1;
}

// Animated counter for elapsed time
const ElapsedTimer = ({ startTime, isActive }) => {
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isActive && startTime) {
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isActive, startTime]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const display = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;

  return (
    <Typography variant="caption" sx={{ color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>
      {display}
    </Typography>
  );
};

const AsyncJobProgress = ({
  jobState,
  progressPercentage,
  formattedElapsedTime,
  isActive,
  isCompleted,
  isFailed,
  onDownload,
  onReset,
  onCancel,
  downloadLoading = false,
}) => {
  const activeStageIdx = getActiveStageIndex(progressPercentage);

  if (!jobState.jobId && !isActive) return null;

  const borderColor = isCompleted
    ? 'rgba(16, 185, 129, 0.3)'
    : isFailed
    ? 'rgba(239, 68, 68, 0.3)'
    : 'rgba(99, 102, 241, 0.25)';

  const bgGradient = isCompleted
    ? 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(5,150,105,0.04) 100%)'
    : isFailed
    ? 'linear-gradient(135deg, rgba(239,68,68,0.06) 0%, rgba(220,38,38,0.04) 100%)'
    : 'linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(139,92,246,0.04) 100%)';

  return (
    <Fade in timeout={400}>
      <Paper
        elevation={0}
        sx={{
          mt: 4,
          borderRadius: '20px',
          border: `1px solid ${borderColor}`,
          background: bgGradient,
          overflow: 'hidden',
        }}
      >
        {/* Top accent bar */}
        <Box
          sx={{
            height: 3,
            background: isCompleted
              ? 'linear-gradient(90deg, #10b981, #34d399)'
              : isFailed
              ? 'linear-gradient(90deg, #ef4444, #f87171)'
              : 'linear-gradient(90deg, #6366f1, #a855f7, #ec4899)',
            backgroundSize: isActive ? '200% 100%' : '100% 100%',
            animation: isActive ? 'slide-gradient 2s linear infinite' : 'none',
            '@keyframes slide-gradient': {
              '0%': { backgroundPosition: '0% 0%' },
              '100%': { backgroundPosition: '200% 0%' },
            },
          }}
        />

        <Box sx={{ p: { xs: 3, md: 4 } }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isActive && (
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: '#6366f1',
                    animation: 'blink 1.2s ease-in-out infinite',
                    '@keyframes blink': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.4, transform: 'scale(0.8)' },
                    },
                  }}
                />
              )}
              {isCompleted && <CheckIcon sx={{ color: '#10b981', fontSize: 20 }} />}
              {isFailed && <ErrorIcon sx={{ color: '#ef4444', fontSize: 20 }} />}
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
                {isCompleted
                  ? 'SOW Document Ready'
                  : isFailed
                  ? 'Generation Failed'
                  : 'AI is generating your SOW'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isActive && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ClockIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                  <ElapsedTimer startTime={jobState.startTime} isActive={isActive} />
                </Box>
              )}
              {isCompleted && jobState.result?.generation_time && (
                <Chip
                  icon={<ClockIcon sx={{ fontSize: 14 }} />}
                  label={`${Math.round(jobState.result.generation_time)}s`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(16,185,129,0.1)',
                    color: '#059669',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    border: '1px solid rgba(16,185,129,0.2)',
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Progress bar */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#4b5563', fontWeight: 500, fontSize: '0.875rem' }}>
                {isCompleted
                  ? 'Document generated successfully'
                  : isFailed
                  ? 'An error occurred'
                  : jobState.message || 'Processing...'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#6b7280', fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '0.875rem' }}
              >
                {Math.round(progressPercentage)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(0,0,0,0.07)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: isCompleted
                    ? 'linear-gradient(90deg, #10b981, #34d399)'
                    : isFailed
                    ? '#ef4444'
                    : 'linear-gradient(90deg, #6366f1, #a855f7)',
                  transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                },
              }}
            />
          </Box>

          {/* AI Activity Feed — only while active */}
          {isActive && (
            <Collapse in>
              <Box
                sx={{
                  mb: 3,
                  p: 2.5,
                  borderRadius: '14px',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(99,102,241,0.12)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: '#6366f1',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontSize: '0.7rem',
                    display: 'block',
                    mb: 1.5,
                  }}
                >
                  AI Activity
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {AI_STAGES.map((stage, idx) => {
                    const status =
                      idx < activeStageIdx ? 'done' : idx === activeStageIdx ? 'active' : 'pending';
                    return (
                      <React.Fragment key={idx}>
                        <StageRow stage={stage} status={status} />
                        {idx < AI_STAGES.length - 1 && (
                          <Box
                            sx={{
                              ml: '15px',
                              width: 1.5,
                              height: 10,
                              backgroundColor:
                                idx < activeStageIdx
                                  ? 'rgba(16,185,129,0.3)'
                                  : 'rgba(0,0,0,0.07)',
                              transition: 'background-color 0.4s ease',
                            }}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>

              {/* Estimated time */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.25,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(99,102,241,0.06)',
                  border: '1px solid rgba(99,102,241,0.1)',
                  mb: 3,
                }}
              >
                <ClockIcon sx={{ fontSize: 15, color: '#6366f1' }} />
                <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 500 }}>
                  Typical generation time is{' '}
                  <strong style={{ color: '#6366f1' }}>2–3 minutes</strong>. Please keep this tab open.
                </Typography>
              </Box>
            </Collapse>
          )}

          {/* Error */}
          {isFailed && jobState.error && (
            <Collapse in>
              <Alert
                severity="error"
                sx={{ mb: 3, borderRadius: '12px', fontSize: '0.875rem' }}
              >
                <strong>Error:</strong> {jobState.error}
              </Alert>
            </Collapse>
          )}

          {/* Success details */}
          {isCompleted && jobState.result && (
            <Collapse in>
              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: '14px',
                  backgroundColor: 'rgba(16,185,129,0.06)',
                  border: '1px solid rgba(16,185,129,0.2)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CheckIcon sx={{ color: '#10b981', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#065f46' }}>
                    Your SOW document is ready to download
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5, borderColor: 'rgba(16,185,129,0.15)' }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {jobState.result.customer_name && (
                    <Chip
                      icon={<PersonIcon sx={{ color: '#1e1e1e !important', fontSize: 13 }} />}
                      label={`Customer: ${jobState.result.customer_name}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        px: 0.5,
                        color: '#1e1e1e',
                        backgroundColor: 'rgba(180, 228, 206, 0.15)',
                        borderColor: '#b4e4ce',
                        '& .MuiChip-icon': { color: '#1e1e1e', fontSize: 13 },
                      }}
                    />
                  )}
                  {jobState.result.project_type && (
                    <Chip
                      icon={<ProjectIcon sx={{ color: '#1e1e1e !important', fontSize: 13 }} />}
                      label={`Type: ${jobState.result.project_type}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        px: 0.5,
                        color: '#1e1e1e',
                        backgroundColor: 'rgba(180, 228, 206, 0.15)',
                        borderColor: '#b4e4ce',
                        '& .MuiChip-icon': { color: '#1e1e1e', fontSize: 13 },
                      }}
                    />
                  )}
                  {jobState.result.generation_time && (
                    <Chip
                      icon={<TimerIcon sx={{ color: '#1e1e1e !important', fontSize: 13 }} />}
                      label={`Generated in ${Math.round(jobState.result.generation_time)}s`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        px: 0.5,
                        color: '#1e1e1e',
                        backgroundColor: 'rgba(180, 228, 206, 0.15)',
                        borderColor: '#b4e4ce',
                        '& .MuiChip-icon': { color: '#1e1e1e', fontSize: 13 },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Collapse>
          )}

          {/* Job ID */}
          {jobState.jobId && (
            <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mb: 2, fontFamily: 'monospace' }}>
              Job ID: {jobState.jobId}
            </Typography>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {isActive && onCancel && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={onCancel}
                size="small"
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                Cancel
              </Button>
            )}
            {(isCompleted || isFailed) && onReset && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onReset}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                Generate New SOW
              </Button>
            )}
            {isCompleted && onDownload && (
              <Button
                variant="contained"
                startIcon={downloadLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                onClick={onDownload}
                disabled={downloadLoading}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    boxShadow: '0 6px 20px rgba(16,185,129,0.45)',
                  },
                }}
              >
                {downloadLoading ? 'Downloading...' : 'Download SOW'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Fade>
  );
};

export default AsyncJobProgress;
